import { useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import {
  CONVERSATION_TITLE_MAX_LENGTH,
  type ConversationSummary
} from '@axilon-apps/domains-ontology-ui-state'

import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { ResourceShareButton } from '../ResourceSharingDialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { ScrollArea } from '../ui/scroll-area'
import { CHAT_SHARE_ALLOWED_ACCESS_LEVELS, CHAT_SHARE_SCOPE_DESCRIPTION } from './chat-behavior'
import { groupConversationsByRecency } from './conversation-groups'
import { CurrentIngestEntry, type CurrentIngestDescriptor } from './CurrentIngestEntry'
import { ReadOnlyConversationBadge } from './ReadOnlyConversationBadge'

export interface ConversationListProps {
  conversations: ConversationSummary[]
  conversationsLoaded: boolean
  activeTabId: string | null
  openTabIds: string[]
  /** Optional action for the side rail. The full chat page owns its header action. */
  onNewTab?: () => void
  onSelect: (id: string) => void
  onRename: (id: string, title: string) => void
  onDelete: (id: string) => void
  /**
   * Active ingest run, pinned above the recency groups. Search does not filter
   * it — it is the one row that is about live work rather than history.
   */
  currentIngest?: CurrentIngestDescriptor
}

export function ConversationList({
  conversations,
  conversationsLoaded,
  activeTabId,
  openTabIds,
  onNewTab,
  onSelect,
  onRename,
  onDelete,
  currentIngest
}: ConversationListProps) {
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle.length === 0) return conversations
    return conversations.filter((conversation) => conversation.title.toLowerCase().includes(needle))
  }, [conversations, query])
  const groups = useMemo(() => groupConversationsByRecency(filtered), [filtered])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid gap-2 px-3 pt-3 pb-2">
        {onNewTab ? (
          <Button
            type="button"
            variant="outline"
            onClick={onNewTab}
            className="h-9 w-full justify-start font-medium hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
          >
            <Plus className="size-4" />
            New chat
          </Button>
        ) : null}
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search"
          aria-label="Search conversations"
          className="h-8 bg-muted/50 text-sm"
        />
      </div>
      <ScrollArea className="min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]>div]:!block">
        <div className="space-y-3 px-2 pb-2">
          {currentIngest ? <CurrentIngestEntry {...currentIngest} /> : null}
          {!conversationsLoaded ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Loading...</p>
          ) : conversations.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              No conversations yet. Submit a prompt to start your first one.
            </p>
          ) : groups.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              No conversations match that search.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.id}>
                <div className="px-2 py-1 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {group.conversations.map((conversation) => (
                    <ConversationRow
                      key={conversation.id}
                      conversation={conversation}
                      active={conversation.id === activeTabId}
                      open={openTabIds.includes(conversation.id)}
                      onSelect={() => onSelect(conversation.id)}
                      onRename={(title) => onRename(conversation.id, title)}
                      onDelete={() => onDelete(conversation.id)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function ConversationRow({
  conversation,
  active,
  open,
  onSelect,
  onRename,
  onDelete
}: {
  conversation: ConversationSummary
  active: boolean
  open: boolean
  onSelect: () => void
  onRename: (title: string) => void
  onDelete: () => void
}) {
  const canContribute = conversation.canContribute === true
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(conversation.title)
  const skipCommitRef = useRef(false)

  const beginEdit = (event?: { stopPropagation: () => void }): void => {
    event?.stopPropagation()
    skipCommitRef.current = false
    setDraft(conversation.title)
    setEditing(true)
  }

  const cancelEdit = (): void => {
    skipCommitRef.current = true
    setDraft(conversation.title)
    setEditing(false)
  }

  const commitEdit = (): void => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false
      return
    }
    skipCommitRef.current = true
    const next = draft.trim()
    setEditing(false)
    if (next.length === 0 || next === conversation.title) {
      setDraft(conversation.title)
      return
    }
    onRename(next)
  }

  return (
    <div
      {...(editing
        ? {}
        : {
            role: 'button' as const,
            tabIndex: 0,
            onClick: onSelect,
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect()
              }
            }
          })}
      aria-current={active ? 'true' : undefined}
      className={cn(
        // Brand fill marks the open chat beside the faint hover wash.
        'group flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors',
        editing ? 'cursor-default' : 'cursor-pointer',
        active ? 'bg-brand-soft hover:bg-brand-soft' : 'hover:bg-foreground/5'
      )}
    >
      <span className="min-w-0 flex-1">
        {editing ? (
          <Input
            value={draft}
            maxLength={CONVERSATION_TITLE_MAX_LENGTH}
            aria-label="Conversation title"
            autoFocus
            className="h-7 px-2 text-sm font-medium"
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={(event) => event.currentTarget.select()}
            onBlur={commitEdit}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === 'Enter') {
                event.preventDefault()
                commitEdit()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                cancelEdit()
              }
            }}
          />
        ) : (
          <span
            className="flex min-w-0 items-center gap-1.5"
            onDoubleClick={canContribute ? beginEdit : undefined}
          >
            <span
              className={cn(
                'min-w-0 truncate text-sm',
                active ? 'font-semibold text-brand' : 'font-medium text-foreground'
              )}
            >
              {conversation.title}
            </span>
            {!canContribute ? <ReadOnlyConversationBadge /> : null}
          </span>
        )}
        <span className="block text-xs text-muted-foreground">
          {formatRelativeTime(conversation.updated_at)} · {conversation.turn_count}{' '}
          {conversation.turn_count === 1 ? 'turn' : 'turns'}
          {open ? ' · open' : ''}
        </span>
      </span>
      <ResourceShareButton
        resourceName={conversation.title}
        resourceLabel="conversation"
        accessScopeId={conversation.accessScopeId}
        allowedAccessLevels={CHAT_SHARE_ALLOWED_ACCESS_LEVELS}
        scopeDescription={CHAT_SHARE_SCOPE_DESCRIPTION}
        compact
      />
      {!canContribute || editing ? null : (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="opacity-0 group-hover:opacity-100"
          aria-label={`Rename conversation "${conversation.title}"`}
          onClick={beginEdit}
        >
          <Pencil className="size-3" />
        </Button>
      )}
      {canContribute ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="opacity-0 group-hover:opacity-100"
          aria-label={`Delete conversation "${conversation.title}"`}
          onClick={(event) => {
            event.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 className="size-3 text-destructive" />
        </Button>
      ) : null}
    </div>
  )
}
